import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, User } from "lucide-react";
import AuthImage from "@/components/AuthImage";
import ProfileDialog from "@/components/ProfileDialog";

const ROLE_LABEL = { admin: "Gestor Global", staff: "Equipa", client: "" };

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isClient = user?.role === "client";

  return (
    <div className="min-h-screen bg-[#F9F6F0] text-[#1A1412]">
      <header className="border-b border-[#EAE3DB] bg-[#F9F6F0]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:py-5 flex items-center justify-between gap-2">
          <Link to="/" className="group flex items-center gap-2 sm:gap-3 min-w-0" data-testid="brand-home-link">
            {isClient && user?.logo_path ? (
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white border border-[#EAE3DB] overflow-hidden flex items-center justify-center flex-shrink-0">
                <AuthImage path={user.logo_path} className="w-full h-full object-contain p-1" />
              </div>
            ) : (
              <img
                src="/unlimited-logo.png"
                alt="Unlimited Studio"
                className="w-8 h-8 sm:w-9 sm:h-9 object-contain rounded-full bg-[#3B2B23] p-1.5 flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[#5C4A43] truncate">
                {isClient ? (user?.company || "A minha marca") : "Unlimited Studio"}
              </div>
              <div className="text-xs sm:text-sm font-bold text-[#1A1412] -mt-0.5 tracking-wide truncate">
                {isClient ? "O meu planner" : "Planner"}
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {user && (
              <button
                onClick={() => setProfileOpen(true)}
                className="flex items-center gap-2 sm:gap-3 hover:bg-[#EAE3DB]/60 rounded-full pl-2 sm:pl-3 pr-2 sm:pr-4 py-1.5 sm:py-2 transition-colors"
                data-testid="header-profile-button"
              >
                <div className="text-right hidden md:block">
                  <div className="text-sm font-bold tracking-wide">{user.name}</div>
                  <div className="text-[10px] text-[#5C4A43] uppercase tracking-widest">
                    {ROLE_LABEL[user.role] || user.company || "Cliente"}
                  </div>
                </div>
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#3B2B23] text-[#F9F6F0] flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#5C4A43] hover:text-[#3B2B23] transition-colors px-2 sm:px-3 py-2 rounded-full hover:bg-[#EAE3DB]/60"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10 lg:py-14">
        {(title || subtitle || actions) && (
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 md:gap-6 mb-8 md:mb-10 fade-up">
            <div>
              {subtitle && (
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5E3C] mb-2 sm:mb-3">{subtitle}</div>
              )}
              {title && (
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-[#1A1412] leading-[1.05]">
                  {title}
                </h1>
              )}
            </div>
            {actions && <div className="flex flex-wrap gap-2 sm:gap-3">{actions}</div>}
          </div>
        )}
        {children}
      </main>

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}
